import React from "react";
import { motion } from "framer-motion";
import { Compass, Brain, Sparkles, Globe2 } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { TALENT_DISCIPLINES, TALENT_VALUES, IMAGES } from "@/data/content";

const valueIcons = [Compass, Brain, Sparkles, Globe2];

export const TalentNetworkSection = () => {
    return (
        <section
            id="talent"
            className="relative py-24 lg:py-32"
            aria-labelledby="talent-heading"
        >
            <div className="mx-auto max-w-7xl container-px">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start mb-14">
                    <div className="lg:col-span-7">
                        <SectionHeading
                            eyebrow="Global Talent Network"
                            title={
                                <>
                                    A curated network of operators —{" "}
                                    <span className="italic text-[hsl(var(--text-muted))]">not a marketplace.</span>
                                </>
                            }
                            subtitle="Senior strategists, creative directors, designers, and engineers — vetted, briefed by AI, and orchestrated by people who’ve actually shipped."
                        />
                    </div>
                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="relative overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-soft aspect-[4/3]"
                        >
                            <img
                                src={IMAGES.talentNetwork}
                                alt="Curated expert network"
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="lazy"
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(255,255,255,0) 55%, rgba(255,255,255,0.55) 100%)",
                                }}
                            />
                            <div className="absolute bottom-4 left-4 right-4 rounded-2xl glass-strong p-3.5">
                                <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                    Operating model
                                </div>
                                <div className="font-display text-lg text-[hsl(var(--text))] mt-1">
                                    AI accelerates. Humans elevate.
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {TALENT_DISCIPLINES.map((d, i) => (
                            <motion.div
                                key={d.title}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-10% 0px" }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.04,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                data-testid="talent-discipline-card"
                                className="group rounded-2xl border border-black/[0.07] bg-white p-5 hover:border-black/15 hover:shadow-soft transition-all duration-300"
                            >
                                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                    Discipline
                                </div>
                                <h4 className="mt-1.5 font-display text-xl text-[hsl(var(--text))]">
                                    {d.title}
                                </h4>
                                <p className="mt-2 text-[13.5px] text-[hsl(var(--text-muted))] leading-relaxed">
                                    {d.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                    <div className="lg:col-span-5 flex flex-col gap-4">
                        {TALENT_VALUES.map((v, i) => {
                            const Icon = valueIcons[i % valueIcons.length];
                            return (
                                <motion.div
                                    key={v.title}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-10% 0px" }}
                                    transition={{
                                        duration: 0.5,
                                        delay: i * 0.05,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="flex items-start gap-4 rounded-2xl border border-black/[0.07] bg-white/70 p-5"
                                >
                                    <span className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(187,118,33,0.10)] ring-1 ring-[rgba(187,118,33,0.35)] text-[hsl(var(--gold))]">
                                        <Icon className="h-4 w-4" />
                                    </span>
                                    <div>
                                        <h5 className="font-display text-lg text-[hsl(var(--text))]">
                                            {v.title}
                                        </h5>
                                        <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed">
                                            {v.description}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TalentNetworkSection;
